import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Rocket, Loader2 } from "lucide-react";
import { AddSuretyLead } from "@/components/AddSuretyLead";
import { SuretyLeadImport } from "@/components/SuretyLeadImport";
import { SuretyAutopilotCard } from "@/components/SuretyAutopilotCard";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const RECENCY = [[7, "Last week"], [30, "Last 30 days"], [90, "Last 90 days"], [180, "Last 6 months"], [365, "Last year"], [0, "All active licensees"]];
const STATUS = { awaiting_payment: "bg-amber-100 text-amber-800", processing: "bg-sky-100 text-sky-800", completed: "bg-emerald-100 text-emerald-800", failed: "bg-red-100 text-red-800" };

const pay = async (id) => {
  try { const { data } = await api.post(`/surety/runs/${id}/checkout`, { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
  catch (e) { toast.error(apiError(e)); }
};

const NewRun = ({ opts, onCreated }) => {
  const [f, setF] = useState({ lead_count: 25, license_types: ["CGC", "CBC", "CRC"], days: 90, prospect: "licensed", cities: "", send_emails: true, send_postcards: false, skip_bonded: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (c) => set("license_types", f.license_types.includes(c) ? f.license_types.filter((x) => x !== c) : [...f.license_types, c]);
  const p = opts.pricing;
  const total = p.run_price + (f.send_postcards ? p.postcard_price * Number(f.lead_count || 0) : 0);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/surety/runs", { lead_count: Number(f.lead_count), license_types: f.license_types, licensed_within_days: f.prospect === "licensed" ? f.days || null : null, prospect: f.prospect, cities: f.cities, send_emails: f.send_emails, send_postcards: f.send_postcards, skip_bonded: f.prospect === "licensed" && f.skip_bonded });
      onCreated();
      if (data.status === "awaiting_payment") await pay(data.id); else toast.success("Run started");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Card className="mb-6 p-6" data-testid="surety-new-run">
      <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Rocket className="h-5 w-5" />New contractor lead run</h3>
      <p className="mb-4 text-sm text-slate-600">AI pulls actively licensed Florida contractors from the DBPR, finds their email with AvaData, and sends each one a personal email from you about surety bonds they may need for licensing. Optional postcards go to their license address. No calls are made.</p>
      <div className="mb-4 flex flex-wrap gap-2" data-testid="surety-prospect">
        {[["licensed", "Licensed contractors"], ["exam", "Eligible for the license exam"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => set("prospect", v)} data-testid={`surety-prospect-${v}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${f.prospect === v ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
        {f.prospect === "exam" && <span className="self-center text-xs text-slate-500">Applicants DBPR approved this week to sit for their contractor exam (updated weekly).</span>}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <div><Label className="text-xs">Number of leads (max {p.max_leads})</Label><Input type="number" min="1" max={p.max_leads} value={f.lead_count} onChange={(e) => set("lead_count", e.target.value)} data-testid="surety-lead-count" /></div>
        {f.prospect === "licensed" && <div><Label className="text-xs">Licensed within</Label>
          <select className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm" value={f.days} onChange={(e) => set("days", Number(e.target.value))} data-testid="surety-recency">
            {RECENCY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>}
        <div><Label className="text-xs">Cities (optional, comma separated)</Label><Input value={f.cities} onChange={(e) => set("cities", e.target.value)} placeholder="Orlando, Tampa" data-testid="surety-cities" /></div>
      </div>
      <Label className="mt-4 block text-xs">License types</Label>
      <div className="mt-1 flex flex-wrap gap-2" data-testid="surety-license-types">
        {opts.license_types.map((t) => (
          <button key={t.code} type="button" onClick={() => toggle(t.code)} data-testid={`surety-type-${t.code}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${f.license_types.includes(t.code) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"}`}>{t.label}</button>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.send_emails} onCheckedChange={(v) => set("send_emails", v)} data-testid="surety-send-emails" />Send AI emails</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.send_postcards} onCheckedChange={(v) => set("send_postcards", v)} data-testid="surety-send-postcards" />Mail postcards (+{money(p.postcard_price)} each)</label>
        {f.prospect === "licensed" && opts.bond_check_ready && <label className="flex items-center gap-2 text-sm" title={opts.bond_check_ready ? "" : "Needs the StateLicense bond data service connected"}>
          <Switch checked={f.skip_bonded} disabled={!opts.bond_check_ready} onCheckedChange={(v) => set("skip_bonded", v)} data-testid="surety-skip-bonded" />
          Skip contractors already bonded on public record{!opts.bond_check_ready && <span className="text-xs text-amber-700" data-testid="surety-skip-bonded-off">(bond data service not connected yet)</span>}</label>}
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-4">
        <Button onClick={submit} disabled={busy || !f.license_types.length} data-testid="surety-start-run">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Start run · {money(total)}</Button>
        <span className="text-xs text-slate-500">Finish <Link to="/surety/profile" className="font-semibold text-amber-700 underline">your agent profile</Link> (name, NPN, carrier{f.send_postcards ? ", mailing address" : ""}) first.</span>
      </div>
    </Card>
  );
};

const RunRow = ({ r }) => (
  <div className="flex flex-col gap-2 border-t border-slate-100 py-3 text-sm md:flex-row md:items-center" data-testid={`surety-run-${r.id}`}>
    <div className="md:w-1/3"><div className="font-semibold text-slate-900">{r.lead_count} leads · {r.license_types.join(", ")}</div><div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()} · {r.prospect === "exam" ? "eligible for exam" : r.licensed_within_days ? `licensed in last ${r.licensed_within_days} days` : "all active"}{r.cities ? ` · ${r.cities}` : ""}</div></div>
    <div className="md:w-1/3 text-xs text-slate-600" data-testid={`surety-run-stats-${r.id}`}>{r.found} found · {r.emails_found} emails found · {r.emailed} emailed · {r.mailed} postcards{r.note ? ` · ${r.note}` : ""}{r.error ? ` · ${r.error}` : ""}</div>
    <div className="flex items-center gap-2 md:flex-1 md:justify-end">
      <Badge className={`border-0 capitalize ${STATUS[r.status]}`}>{r.status.replace("_", " ")}</Badge>
      {r.status === "awaiting_payment" && <Button size="sm" onClick={() => pay(r.id)} data-testid={`surety-pay-${r.id}`}>Pay {money(r.price)}</Button>}
      {r.found > 0 && <Link to={`/surety/leads?run=${r.id}`} className="text-xs font-semibold text-amber-700 underline">View leads</Link>}
    </div>
  </div>
);

export default function SuretyRuns() {
  const [opts, setOpts] = useState(null);
  const [runs, setRuns] = useState([]);
  const load = () => api.get("/surety/runs").then((r) => setRuns(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/surety/options").then((r) => setOpts(r.data)).catch((e) => toast.error(apiError(e)));
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="surety-runs-page">
      <PageHeader title="Contractor Lead Runs" subtitle="Find Florida licensed contractors who may need surety bonds." />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="surety-runs-add-leads">
        <AddSuretyLead />
        <SuretyLeadImport />
        <Button asChild variant="ghost"><Link to="/surety/leads">View contractor leads</Link></Button>
      </div>
      <SuretyAutopilotCard />
      {opts && <NewRun opts={opts} onCreated={load} />}
      <Card className="p-6"><h3 className="mb-2 font-display text-lg font-bold text-slate-900">Your runs</h3>
        {!runs.length && <p className="text-sm text-slate-500" data-testid="surety-runs-empty">No runs yet.</p>}
        {runs.map((r) => <RunRow key={r.id} r={r} />)}
      </Card>
    </div>
  );
}
